import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../../components/AtiraIcon";
import { Button } from "../../../../components/Button";
import { FontSize, Text } from "../../../../components/Text";
import { DashboardSetupResponseDto } from "../../../../model/dashboard/dto/DashboardSetupResponseDto";
import { DashboardSetupStep } from "../../../../model/dashboard/types/DashboardSetupStep.enum";
import { dashboardSliceSelectors } from "../../../../redux/dashboard/dashboard.selector";
import { useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { TestIDs } from "../../../../tests/types/testIds";
import { Breakpoints } from "../../../../theme/Breakpoints";
import { Rounded } from "../../../../theme/Rounded";
import { Shadow } from "../../../../theme/Shadow";
import { Spacing } from "../../../../theme/Spacing";
import { truncateString } from "../../../../utils/String";
import { DASHBOARD_SETUP_STEPS, getAppCardSpan } from "../../DashboardUtils";
import { DashboardColumns } from "../DashboardPanel";
import { DashboardTasksDue } from "../DashboardTasksDue";
import { WidgetLatestEntries } from "../widgets/WidgetLatestEntries";
import { DashboardSetupAppCard } from "./DashboardSetupAppCard";
import { DashboardSetupDealCard } from "./DashboardSetupDealCard";
import { DashboardSetupEntryCard } from "./DashboardSetupEntryCard";
import { DashboardSetupFormCard } from "./DashboardSetupFormCard";
import { DashboardSetupTaskCard } from "./DashboardSetupTaskCard";
import { DashboardSetupTeamCard } from "./DashboardSetupTeamCard";
import { DashboardSetupWhatsappCard } from "./DashboardSetupWhatsappCard";

const STEP_CARDS: Record<
  DashboardSetupStep,
  React.FC<{ featured?: boolean; done?: boolean }>
> = {
  [DashboardSetupStep.FORM]: DashboardSetupFormCard,
  [DashboardSetupStep.ENTRY]: DashboardSetupEntryCard,
  [DashboardSetupStep.WHATSAPP]: DashboardSetupWhatsappCard,
  [DashboardSetupStep.TEAM]: DashboardSetupTeamCard,
  [DashboardSetupStep.DEAL]: DashboardSetupDealCard,
  [DashboardSetupStep.TASK]: DashboardSetupTaskCard,
};

const Board = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.m};
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${Spacing.m};
  padding: ${Spacing.m} ${Spacing.l};
  border-radius: ${Rounded.xl};
  background-color: ${({ theme }) => theme.white};
  box-shadow: ${Shadow.SM};
`;

const Ring = styled.div<{ $percent: number }>`
  flex: none;
  display: grid;
  place-items: center;
  width: 3.75rem;
  height: 3.75rem;
  border-radius: ${Rounded.circle};
  background: ${({ theme, $percent }) =>
    `conic-gradient(${theme.main} 0 ${$percent}%, ${theme.sub} ${$percent}% 100%)`};
`;

const RingInner = styled.span`
  display: grid;
  place-items: center;
  width: 2.9rem;
  height: 2.9rem;
  border-radius: ${Rounded.circle};
  background-color: ${({ theme }) => theme.white};
  font-weight: bold;
  font-size: ${FontSize.s};
  font-variant-numeric: tabular-nums;
`;

const Greeting = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  min-width: 0;
  flex: 1 1 16rem;
`;

const DoneChips = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const DoneChip = styled.li`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => `${theme.success}1a`};
  color: ${({ theme }) => theme.success};
  font-size: ${FontSize.xs};
  font-weight: 500;
`;

const Grid = styled.div`
  display: grid;
  gap: ${Spacing.m};
  grid-template-columns: minmax(0, 1.3fr) repeat(2, minmax(0, 1fr));
  align-items: stretch;

  @media (max-width: ${Breakpoints.LARGE_DESKTOP}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: ${Breakpoints.TABLET}) {
    grid-template-columns: minmax(0, 1fr);

    & > * {
      grid-row: auto;
    }
  }
`;

const AllDone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${Spacing.s};
  padding: ${Spacing.l};
  border-radius: ${Rounded.xl};
  background-color: ${({ theme }) => theme.white};
  box-shadow: ${Shadow.SM};
`;

type DashboardSetupBoardProps = {
  setup: DashboardSetupResponseDto;
  onSwitchToToday: VoidFunction;
};

export const DashboardSetupBoard: React.FC<DashboardSetupBoardProps> = ({
  setup,
  onSwitchToToday,
}) => {
  const { t } = useTranslation();
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const today = useAppSelector(dashboardSliceSelectors.selectToday);

  const done = DASHBOARD_SETUP_STEPS.filter((step) => setup.steps[step]);
  const open = DASHBOARD_SETUP_STEPS.filter((step) => !setup.steps[step]);

  // Signing up is the first step, so the bar never starts at zero
  const total = DASHBOARD_SETUP_STEPS.length + 1;
  const completed = done.length + 1;
  const percent = Math.round((completed / total) * 100);
  const tallFirst = open[0] === DashboardSetupStep.FORM;


  return (
    <Board data-testid={TestIDs.dashboard.setup.wrapper}>
      <Header>
        <Ring
          $percent={percent}
          role="img"
          aria-label={t("dashboard.setup.progress", {
            done: completed,
            total,
          })}
        >
          <RingInner aria-hidden>{`${completed}/${total}`}</RingInner>
        </Ring>

        <Greeting>
          <Text fontSize="l" fontWeight="bold">
            {t("dashboard.setup.greeting", {
              name: truncateString(user.name),
            })}
          </Text>
          <Text color="darkerSub">{t("dashboard.setup.subtitle")}</Text>
        </Greeting>

        <DoneChips aria-label={t("dashboard.setup.done_label")}>
          <DoneChip>
            <AtiraIcon icon={faCheck} size="xs" />
            {t("dashboard.setup.done.ACCOUNT")}
          </DoneChip>
          {done.map((step) => (
            <DoneChip key={step}>
              <AtiraIcon icon={faCheck} size="xs" />
              {t(`dashboard.setup.done.${step}`)}
            </DoneChip>
          ))}
        </DoneChips>
      </Header>

      {!open.length ? (
        <AllDone>
          <Text fontSize="xl" fontWeight="bold">
            {t("dashboard.setup.all_done.title")}
          </Text>
          <Text color="darkerSub">
            {t("dashboard.setup.all_done.description")}
          </Text>
          <Button
            onClick={onSwitchToToday}
            dataTestId={TestIDs.dashboard.setup.switchToToday}
          >
            {t("dashboard.setup.all_done.cta")}
          </Button>
        </AllDone>
      ) : null}

      <Grid>
        {[...open, ...done].map((step, index) => {
          const Card = STEP_CARDS[step];
          return (
            <Card
              key={step}
              featured={index === 0 && open.length > 0}
              done={setup.steps[step]}
            />
          );
        })}
        <DashboardSetupAppCard
          span3={getAppCardSpan(DASHBOARD_SETUP_STEPS.length, tallFirst, 3)}
          span2={getAppCardSpan(DASHBOARD_SETUP_STEPS.length, tallFirst, 2)}
        />
      </Grid>

      <DashboardColumns>
        <WidgetLatestEntries />
        <DashboardTasksDue today={today} />
      </DashboardColumns>
    </Board>
  );
};
